import { createContext, useContext, type ReactNode } from 'react';
import type { PortfolioClient } from '@personal-portfolio/api-client';

const PortfolioClientContext = createContext<PortfolioClient | null>(null);

export interface PortfolioClientProviderProps {
  client: PortfolioClient;
  children: ReactNode;
}

export const PortfolioClientProvider = ({
  client,
  children,
}: PortfolioClientProviderProps) => (
  <PortfolioClientContext.Provider value={client}>
    {children}
  </PortfolioClientContext.Provider>
);

/**
 * Throws when called outside a `<PortfolioClientProvider>`, rather than
 * returning null. Every hook in this lib depends on it, so a missing provider
 * is a wiring bug that should fail loudly at first render instead of surfacing
 * later as an unexplained null dereference inside a query function.
 */
export const usePortfolioClient = (): PortfolioClient => {
  const client = useContext(PortfolioClientContext);
  if (!client) {
    throw new Error(
      'usePortfolioClient must be used within a <PortfolioClientProvider>',
    );
  }
  return client;
};
